import React from "react";
import PropTypes from "prop-types";
import {
  Message,
  MessageHeader,
  Segment,
  Header,
  SegmentGroup,
} from "semantic-ui-react";
import {
  ActionMenu,
  ExternalWorkflowHistory,
  getReducedWorkflowHistory,
  CollaboratorEditor,
  StateDisplay,
  EmbargoSettings,
} from "@components_common/components/utils";
import { Loader } from "semantic-ui-react";
import { QueryClient } from "@tanstack/react-query";
import { useGetDraftAndWorkflows } from "./hooks";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { useFeature } from "@components_common/hooks";
import { useGetDraftRequestsWithRefetch } from "../common";

const CollaboratorsFeatureName = "COLLABORATORS_ENABLED";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

export const AdminPanel = ({ draftId, isAdmin, communityRole }) => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <AdminPanelContent
        draftId={draftId}
        isAdmin={isAdmin}
        communityRole={communityRole}
      />
    </PersistQueryClientProvider>
  );
};

const AdminPanelContent = ({ draftId, isAdmin, communityRole }) => {
  const { data } = useGetDraftAndWorkflows(draftId);
  const { enabled: collaboratorsEnabled, isLoading: isLoadingFeature } =
    useFeature(CollaboratorsFeatureName);
  const { data: draftRequests, isLoading: isLoadingRequests } =
    useGetDraftRequestsWithRefetch(draftId);
  const isLoading = isLoadingFeature || isLoadingRequests;
  const workflowHistory = getReducedWorkflowHistory(data?.workflows);
  console.log(workflowHistory);

  return data === undefined || isLoading ? (
    <Loader active inline size="mini" indeterminate />
  ) : (
    <>
      {workflowHistory &&
        workflowHistory[0]?.status === "Failed" &&
        data?.draft.state === "running" && (
          <Message negative>
            <MessageHeader>A Quality check error has occurred</MessageHeader>
            The quality check has completed but was not reported to the DAR.
            Please contact the administrator to resolve this issue.
          </Message>
        )}
      <SegmentGroup>
        <Segment>
          <Header as="h4">Draft State</Header>
          <StateDisplay state={data?.draft.state} requests={draftRequests} />
        </Segment>
        <Segment>
          <Header as="h4">Action Menu</Header>
          <ActionMenu
            draftId={draftId}
            draftRequests={draftRequests}
            isLoading={isLoadingRequests}
            draft={data.draft}
            isAdmin={isAdmin}
          />
        </Segment>
        <Segment>
          <Header as="h4">Quality Checks History</Header>
          <ExternalWorkflowHistory history={workflowHistory} />
        </Segment>
        {data?.draft.files.enabled && (
          <Segment>
            <Header as="h4">Embargo settings</Header>
            <EmbargoSettings draft={data?.draft} />
          </Segment>
        )}
        {!isLoading && collaboratorsEnabled && (
          <Segment>
            <Header as="h4">Collaborators - PREVIEW - WORK IN PROGRESS</Header>
            <CollaboratorEditor
              collaborators={["Alice", "Bob"]}
              onAdd={(newCollaborators) =>
                console.log("Add:", newCollaborators)
              }
              onRemove={(collaborator) => console.log("Remove:", collaborator)}
              allUsers={["Alice", "Bob", "Charlie", "Dave"]}
            />
          </Segment>
        )}
      </SegmentGroup>
    </>
  );
};

AdminPanelContent.propTypes = {
  draftId: PropTypes.string,
  isAdmin: PropTypes.bool,
};

AdminPanel.propTypes = {
  draftId: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool,
};
